/**
 * Small display pieces shared by the Sandboxes v2 list sections: version
 * labels, readiness chips and the sandbox status chip.
 *
 * @module features/sandboxes/components/v2/sandboxV2Display
 */
import { PulsingDot, TagBadge, type TagColor } from '@chatsdet/ui';
import { shortSandboxV2Version } from '../../../../hooks/useSandboxV2EnvironmentReadiness';
import type { SandboxV2Application, SandboxV2SandboxStatus } from '../../hooks/useSandboxesV2';
import type { SandboxV2RunStatus } from '../../hooks/useSandboxV2Detail';

export const shortVersion = shortSandboxV2Version;

/** A version's tags as pushed, else its short hash or id. */
export function versionName(tags: readonly string[], version: string): string {
  return tags.length > 0 ? tags.join(', ') : shortVersion(version);
}

const READINESS: Record<string, { label: string; color: TagColor }> = {
  ready: { label: 'Ready', color: 'green' },
  warming: { label: 'Warming', color: 'blue' },
  stale: { label: 'Stale', color: 'yellow' },
  none: { label: 'No snapshot', color: 'gray' },
};

export function ReadinessChip({ status, error }: { status: string; error: string | null }) {
  const cfg = READINESS[status] ?? READINESS.none;
  return (
    <TagBadge
      name={cfg.label}
      color={cfg.color}
      size="sm"
      tooltip={error ?? undefined}
      icon={status === 'warming' ? <PulsingDot colorClassName="bg-blue-500" sizeClassName="h-1.5 w-1.5" /> : undefined}
    />
  );
}

const SANDBOX_STATUS: Record<SandboxV2SandboxStatus, { label: string; color: TagColor }> = {
  claiming: { label: 'Claiming', color: 'blue' },
  ready: { label: 'Ready', color: 'green' },
  failed: { label: 'Failed', color: 'red' },
  destroyed: { label: 'Destroyed', color: 'gray' },
};

export function SandboxStatusChip({ status, error }: { status: SandboxV2SandboxStatus; error: string | null }) {
  const cfg = SANDBOX_STATUS[status];
  return (
    <TagBadge
      name={cfg.label}
      color={cfg.color}
      size="sm"
      tooltip={status === 'failed' ? (error ?? 'The claim failed') : undefined}
      icon={status === 'claiming' ? <PulsingDot colorClassName="bg-blue-500" sizeClassName="h-1.5 w-1.5" /> : undefined}
      hideDot={status !== 'claiming'}
    />
  );
}

const RUN_STATUS: Record<SandboxV2RunStatus, { label: string; color: TagColor }> = {
  running: { label: 'Running', color: 'blue' },
  succeeded: { label: 'Succeeded', color: 'green' },
  failed: { label: 'Failed', color: 'red' },
};

export function RunStatusChip({ status }: { status: SandboxV2RunStatus }) {
  const cfg = RUN_STATUS[status];
  return (
    <TagBadge
      name={cfg.label}
      color={cfg.color}
      size="sm"
      icon={status === 'running' ? <PulsingDot colorClassName="bg-blue-500" sizeClassName="h-1.5 w-1.5" /> : undefined}
      hideDot={status !== 'running'}
    />
  );
}
